import React, { useContext } from "react";

import { IssuesWrapper } from "@vp/gatsby-pages-issues-tracker-plugin";
import { RatingReviewData, RatingSummary } from "@vp/rating-reviews-component";
import { Box, FlexBox, H1, H2, Hidden, Icon, Link, Typography } from "@vp/swan";
import { CampaignCallout } from "@vp/campaign-callouts";

import MailingServiceDescriptionCallout from "../../../../components/mailingServices/MailingServiceDescriptionCallout";
import { analyticsPageData, analyticsPageName } from "../../../../utils/analyticsConfig";
import MemoizedReactMarkdown from "../../../../components/MemoizedReactMarkdown";
import { PageStaticDataContext } from "../../../../contexts/PageStaticDataContextProvider";
import { ProductPageTabContext } from "../../../../contexts/ProductPageTabContext";
import PluginPlaceholder from "../../../../pluginsInfrastructure/PluginPlaceholder";
import { Placeholder } from "../../../../pluginsInfrastructure/PluginRegistryTypes";
import { ResolvedTab } from "../../../../types/ContentfulTypes";
import { ProductBuilderData } from "../../../../types/ProductBuilderTypes";
import { Translations } from "../../../../utils/Translation";
import OutOfStock from "./OutOfStock";
import TrackingUtil from "../../../../utils/tracking";
import { SpecsAndTemplatesCalloutAnchorLink } from "./SpecsAndTemplatesCalloutAnchorLink";
import { FeedTemplateContext } from "../../../../contexts/FeedTemplateProvider";
import { splitCamelcaseStringInDifferentWords } from "../../../../utils/urlHelper";

const productPageTabId = "product-page-tabs";
const reviewsPageId = "reviews-container";

const scrollToTabs = () => {
  const element = document.getElementById(productPageTabId);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

type OnPageBuilderHeaderProps = {
  builderData: ProductBuilderData;
  reviews: RatingReviewData;
  productName: string;
  description?: string;
  tabData: ResolvedTab[];
  hideReviews?: boolean;
  showCampaignCallouts?: boolean;
};

const OnPageBuilderHeader = (props: OnPageBuilderHeaderProps) => {
  const { productName, builderData, reviews, description, tabData, hideReviews } = props;
  const { setSelectedTabId, setSelectedTabFocus } = useContext(ProductPageTabContext);
  const feedData = useContext(FeedTemplateContext);
  const { locale, mpv } = useContext(PageStaticDataContext);
  const { mpvId, coreProductId } = mpv;

  const ratingSummaryProps = {
    ...reviews?.summary,
    isClickable: true,
    link: `#${reviewsPageId}`,
    trackingData: {
      productId: mpvId,
      coreProductId: coreProductId,
      category: analyticsPageData.category,
      pageSection: analyticsPageData.pageSection,
      pageStage: analyticsPageData.pageStage,
      pageName: analyticsPageName(mpv.mpvId),
    },
  };
  const showReviews = reviews?.summary?.reviewsCount > 0 && !hideReviews;

  let overviewTabIndex: number | undefined = undefined;
  let designSpecsTabIndex: number | undefined = undefined;

  tabData.forEach((tab, index) => {
    if (tab.type === "tabOverview") {
      overviewTabIndex = index;
    }
    if (tab.type === "Design Specifications") {
      designSpecsTabIndex = index;
    }
  });

  const seeDetailsEntry = Translations.getInstance().getTranslationEntry("seeDetails");
  const onOverviewClick = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    if (overviewTabIndex !== undefined) {
      await setSelectedTabId("tab-" + overviewTabIndex);
      setSelectedTabFocus(true);
      scrollToTabs();
    }
  };

  const onDesignSpecsCalloutClick = async (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.preventDefault();
    TrackingUtil.trackDesignSpecsCalloutClick(mpv);
    if (designSpecsTabIndex !== undefined) {
      await setSelectedTabId("tab-" + designSpecsTabIndex);
      setSelectedTabFocus(true);
      scrollToTabs();
    }
  };

  const productDescription = (
    <Typography fontSize="small" mb="between-actions">
      {description && (
        // @ts-ignore
        <MemoizedReactMarkdown
          components={{
            p: ({ node, ...props }) => <span {...props} />,
          }}
        >
          {description}
        </MemoizedReactMarkdown>
      )}
      <br />

      <IssuesWrapper targetNodes={[seeDetailsEntry]} elementName="See Details">
        <Link
          className="see-details"
          onClick={onOverviewClick}
          href={
            overviewTabIndex !== undefined && overviewTabIndex >= 0
              ? `#${productPageTabId}`
              : undefined
          }
        >
          {seeDetailsEntry ?? ""}
        </Link>
      </IssuesWrapper>
    </Typography>
  );

  const specsCalloutLink = Translations.getInstance().getTranslationEntry("specsAndTemplatesLink");
  const SpecsAndTemplatesCallout = designSpecsTabIndex && specsCalloutLink && (
    <FlexBox alignItems="center" flexDirection="row" mb="between-subsections">
      <Box marginRight={"3"}>
        <Icon iconType={"templates"} size="standard" />
      </Box>
      <Box>
        <IssuesWrapper
          targetNodes={[specsCalloutLink]}
          elementName="Specs and templates callout anchor link"
        >
          <Typography
            fontSize={"small"}
            onClick={onDesignSpecsCalloutClick}
            className="see-details"
          >
            <SpecsAndTemplatesCalloutAnchorLink
              designSpecsTabIndex={designSpecsTabIndex}
              productPageTabId={productPageTabId}
              specsCalloutLink={specsCalloutLink}
            />
          </Typography>
        </IssuesWrapper>
      </Box>
    </FlexBox>
  );

  const subtitle = feedData?.productAudience
    ? `For ${splitCamelcaseStringInDifferentWords(feedData!.productAudience)}`
    : undefined;

  return (
    <>
      <FlexBox justifyContent="space-between" alignItems="center">
        <div>
          <H1 className="product-name" fontSkin="title-section">
            {productName}
          </H1>
          {subtitle && <H2 fontSkin="title-subsection">{subtitle}</H2>}
          {showReviews ? (
            <Box mb="4">
              <Link skin="unstyled" href={`#${reviewsPageId}`}>
                <RatingSummary {...ratingSummaryProps} />
              </Link>
            </Box>
          ) : (
            undefined
          )}
          {props.showCampaignCallouts && (
            <Box mb="4">
              <CampaignCallout product={mpvId} className="discount-gel" />
            </Box>
          )}
          <IssuesWrapper targetNodes={[builderData]} elementName="Product Subtext">
            <>
              {builderData.productSubtext && (
                <Box mb="3">
                  <Hidden xs sm>
                    <Typography fontSkin="body-standard" className="discount-text">
                      {builderData.productSubtext}
                    </Typography>
                  </Hidden>
                  <Hidden md lg xl>
                    <Typography fontSkin="body-standard" className="discount-text">
                      {builderData.productSubtext}
                    </Typography>
                  </Hidden>
                </Box>
              )}
            </>
          </IssuesWrapper>
        </div>
        <div>
          <PluginPlaceholder placeholderType={Placeholder.ProductBuilderHeaderRight} />
        </div>
      </FlexBox>
      <OutOfStock />
      {productDescription}
      <MailingServiceDescriptionCallout />
      {SpecsAndTemplatesCallout}
    </>
  );
};

export default OnPageBuilderHeader;
